import styled from 'styled-components'
import { blue, blueDark, red, textMuted, white } from '@/theme/colors'
import { shadows, radii, motion } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { Button } from '@components/Button'
import { Input } from '@components/Field'

/**
 * Shared styles for the admin resource lists (Events, Ideas, Expenses). Rows are
 * clickable and navigate to the matching detail page, so `Row` carries hover +
 * pointer affordances. Users/Trips keep their own (older) style modules.
 */

export const Toolbar = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 20px;
	flex-wrap: wrap;
`

export const SearchInput = styled(Input)`
	flex: 1;
	width: auto;
	min-width: 220px;
	max-width: 360px;
`

export const ResultCount = styled.span`
	color: ${textMuted};
	font-size: ${fontSizes.small};
`

export const TableWrap = styled.div`
	background-color: ${white};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.inner};
	overflow-x: auto;
`

export const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
	font-size: ${fontSizes.base};
`

export const Th = styled.th`
	text-align: left;
	padding: 14px 16px;
	font-weight: 700;
	color: ${textMuted};
	border-bottom: 1px solid ${textMuted}22;
	white-space: nowrap;
`

export const Td = styled.td`
	padding: 12px 16px;
	border-bottom: 1px solid ${textMuted}14;
	color: ${blue};
	white-space: nowrap;
`

export const Row = styled.tr<{ $deleted?: boolean }>`
	cursor: pointer;
	background-color: ${({ $deleted }) => ($deleted ? `${red}10` : 'transparent')};
	transition: background-color ${motion.fast};

	&:hover {
		background-color: ${({ $deleted }) => ($deleted ? `${red}1f` : `${blue}0d`)};
	}

	&:last-child td {
		border-bottom: none;
	}
`

export const Muted = styled.span`
	color: ${textMuted};
`

export const SubText = styled.div`
	color: ${textMuted};
	font-size: ${fontSizes.caption};
	margin-top: 2px;
`

export const DeletedTag = styled.span`
	display: inline-block;
	margin-left: 8px;
	padding: 2px 8px;
	border-radius: ${radii.small};
	background-color: ${red};
	color: ${white};
	font-size: ${fontSizes.micro};
	font-weight: 700;
	vertical-align: middle;
`

/** Shown only while the include-internal toggle is on, to mark excluded accounts. */
export const InternalTag = styled.span`
	display: inline-block;
	margin-left: 8px;
	padding: 2px 8px;
	border-radius: ${radii.small};
	background-color: ${blue};
	color: ${white};
	font-size: ${fontSizes.micro};
	font-weight: 700;
	vertical-align: middle;
`

export const ScopeTag = styled.span<{ $personal?: boolean }>`
	display: inline-block;
	padding: 2px 8px;
	border-radius: ${radii.small};
	background-color: ${({ $personal }) => ($personal ? `${textMuted}22` : `${blue}1a`)};
	color: ${({ $personal }) => ($personal ? textMuted : blueDark)};
	font-size: ${fontSizes.micro};
	font-weight: 700;
`

export const Pagination = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 16px;
	margin-top: 20px;
`

export const PageButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })``

export const PageStatus = styled.span`
	color: ${textMuted};
	font-size: ${fontSizes.small};
`


